import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Search, Refresh, GetApp, ArrowBack, Close } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar, InputAdornment, TextField } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const fmt     = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => {
  if (!d) return '-';
  if (typeof d === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(d)) return d;
  if (typeof d === 'string' && d.startsWith('/Date(')) {
    const ms = parseInt(d.replace(/\/Date\((\d+)[^)]*\)\//, '$1'));
    if (!isNaN(ms)) {
      const dt = new Date(ms);
      return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`;
    }
  }
  const parsed = new Date(d);
  if (isNaN(parsed)) return '-';
  return `${String(parsed.getDate()).padStart(2,'0')}/${String(parsed.getMonth()+1).padStart(2,'0')}/${parsed.getFullYear()}`;
};
const fmtQty  = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmt2Date = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const PRIMARY = '#1c2266';
const PRIMARY_MID = '#3f51b5';
const PRIMARY_LT = '#e8eaf6';
const th  = { padding: '8px 10px', textAlign: 'left',   fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.2)' };
const td  = { padding: '7px 10px', textAlign: 'left',   fontSize: 12, whiteSpace: 'nowrap' };
const tdR = { padding: '7px 10px', textAlign: 'right',  fontSize: 12, whiteSpace: 'nowrap' };
const tdC = { padding: '7px 10px', textAlign: 'center', fontSize: 12, whiteSpace: 'nowrap' };

const iconBtn = {
  border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
  height: 29, width: 29, color: '#fff', flexShrink: 0,
  '&:hover': { background: 'rgba(255,255,255,0.1)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const SaleSummaryBillWise = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate]     = useState(fmt2Date(today));
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [fetched, setFetched]   = useState(false);
  const [search, setSearch]     = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  const fetchReport = async () => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      const res = await fetchData(
        'SaleReportApi',
        `/SaleSummaryBillDetails/${companyId}/${branchId}/${fromDate}/${toDate}`,
        token
      );
      const rows = res.response_data || res || [];
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch sale summary bill wise');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && branchId && token) fetchReport();
  }, [companyId, branchId, token]); // eslint-disable-line

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter(r =>
      (r.BillNo       || '').toLowerCase().includes(q) ||
      (r.CustomerName || '').toLowerCase().includes(q) ||
      (r.GSTNumber    || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  // Totals
//   const totalQty      = filtered.reduce((s, r) => s + (parseFloat(r.TotalQuantity)  || 0), 0);
  const totalTaxable  = filtered.reduce((s, r) => s + (parseFloat(r.TaxableAmount)  || 0), 0);
  const totalCGST     = filtered.reduce((s, r) => s + (parseFloat(r.CGSTAmount)     || 0), 0);
  const totalSGST     = filtered.reduce((s, r) => s + (parseFloat(r.SGSTAmount)     || 0), 0);
  const totalIGST     = filtered.reduce((s, r) => s + (parseFloat(r.IGSTAmount)     || 0), 0);
  const totalDiscount = filtered.reduce((s, r) => s + (parseFloat(r.DiscountAmount) || 0), 0);
  const totalGrand    = filtered.reduce((s, r) => s + (parseFloat(r.GrandTotal)     || 0), 0);

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'SaleSummaryBillWise', 0);
    if (!filtered.length) return;
    const cols = ['#', 'Bill No', 'Date', 'Customer', 'GST No', 'Contact', 'Total Qty', 'Taxable', 'CGST', 'SGST', 'IGST', 'Discount', 'Freight', 'Packing', 'Grand Total', 'Payment', 'Status', 'Vehicle', 'Transporter', 'Remarks'];
    const rows = filtered.map((r, i) => [
      i + 1, r.BillNo, fmtDate(r.BillDate), r.CustomerName, r.GSTNumber || '', r.ContactNumber || '',
      parseFloat(r.TotalQuantity) || 0, parseFloat(r.TaxableAmount) || 0,
      parseFloat(r.CGSTAmount) || 0, parseFloat(r.SGSTAmount) || 0, parseFloat(r.IGSTAmount) || 0,
      parseFloat(r.DiscountAmount) || 0, parseFloat(r.FreightAmount) || 0, parseFloat(r.PackingAmount) || 0,
      parseFloat(r.GrandTotal) || 0, r.PaymentType || '', r.PaymentStatus || '',
      r.VehicleNumber || '', r.TransporterName || '', r.Remarks || '',
    ]);

      await exportStyledExcel({
          reportTitle: 'SALE SUMMARY BILL WISE',
          sheetName: 'Sale Summary',
          fileName: `SaleSummaryBillWise_${fromDate}_${toDate}.xlsx`,
          companyName,
          username,
          fromDate,
          toDate,
          headers: cols,
          rows,
          totalsRow: ['', '', '', '', '', 'Totals', totalTaxable, totalCGST, totalSGST, totalIGST, totalDiscount, '', '', totalGrand, '', '', '', '', ''],
      });
  };

  const paymentColor = (type) => {
    const t = (type || '').toLowerCase();
    if (t.includes('cash'))   return { bg: '#d1fae5', color: '#059669' };
    if (t.includes('card'))   return { bg: '#dbeafe', color: '#1d4ed8' };
    if (t.includes('upi'))    return { bg: '#fef3c7', color: '#d97706' };
    if (t.includes('credit')) return { bg: '#fce7f3', color: '#be185d' };
    return { bg: '#f3f4f6', color: '#6b7280' };
  };

  const statusColor = (s) => {
    const v = (s || '').toLowerCase();
    if (v.includes('paid') || v.includes('complete')) return { bg: '#d1fae5', color: '#059669' };
    if (v.includes('partial'))                         return { bg: '#dbeafe', color: '#1d4ed8' };
    if (v.includes('pending') || v.includes('unpaid')) return { bg: '#fef3c7', color: '#d97706' };
    return { bg: '#f3f4f6', color: '#6b7280' };
  };

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading sale summary bill wise..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #2d3494 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>Sale Summary Bill Wise</span>
          <div style={{ flex: 1, minWidth: 4 }} />
          {searchOpen ? (
            <>
              <TextField autoFocus size="small" placeholder="Search bill, customer..."
                value={search} onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: 'rgba(255,255,255,0.6)' }} /></InputAdornment>,
                  endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')} sx={{ color: 'rgba(255,255,255,0.6)', p: '2px' }}><Close sx={{ fontSize: 14 }} /></IconButton></InputAdornment> : null,
                }}
                sx={{ flexShrink: 0, width: 180, '& .MuiOutlinedInput-root': { height: 29, fontSize: 12, borderRadius: '8px', color: '#fff', '& fieldset': { borderColor: 'rgba(255,255,255,0.4)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.7)' } }, '& input::placeholder': { color: 'rgba(255,255,255,0.5)', opacity: 1 } }}
              />
              <Tooltip title="Close" arrow>
                <IconButton size="small" onClick={() => { setSearchOpen(false); setSearch(''); }} sx={iconBtn}>
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Search" arrow>
              <IconButton size="small" onClick={() => setSearchOpen(true)} sx={iconBtn}>
                <Search sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <input type="date" value={toDate}   onChange={e => setToDate(e.target.value)}   style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Table */}
        <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 130px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 1100 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr style={{ background: PRIMARY_MID, color: 'white' }}>
                <th style={th}>#</th>
                <th style={th}>Bill No</th>
                <th style={th}>Date</th>
                <th style={th}>Customer</th>
                <th style={th}>GST No</th>
                
                <th style={{ ...th, textAlign: 'right' }}>Taxable</th>
                <th style={{ ...th, textAlign: 'right' }}>CGST</th>
                <th style={{ ...th, textAlign: 'right' }}>SGST</th>
                <th style={{ ...th, textAlign: 'right' }}>IGST</th>
                <th style={{ ...th, textAlign: 'right' }}>Discount</th>
                <th style={{ ...th, textAlign: 'right' }}>Freight</th>
                <th style={{ ...th, textAlign: 'right' }}>Grand Total</th>
                <th style={{ ...th, textAlign: 'center' }}>Payment</th>
                <th style={{ ...th, textAlign: 'center' }}>Status</th>
              </tr>
            </thead> 
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={15} style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>
                    {fetched ? 'No records found' : 'Select date range and click refresh'}
                  </td>
                </tr>
              ) : (
                filtered.map((r, i) => {
                  const pc = paymentColor(r.PaymentType);
                  const sc = statusColor(r.PaymentStatus);
                  return (
                    <tr key={r.SaleId || i} style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT, borderBottom: '1px solid #f1f5f9' }}>
                      <td style={td}>{i + 1}</td>
                      <td style={{ ...td, fontWeight: 700, color: PRIMARY }}>{r.BillNo}</td>
                      <td style={td}>{fmtDate(r.BillDate)}</td>
                      <td style={{ ...td, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.CustomerName}</td>
                      <td style={{ ...td, color: '#64748b', fontSize: 11 }}>{r.GSTNumber || '-'}</td>
                      
                      <td style={tdR}>{fmt(r.TaxableAmount)}</td>
                      <td style={tdR}>{fmt(r.CGSTAmount)}</td>
                      <td style={tdR}>{fmt(r.SGSTAmount)}</td>
                      <td style={tdR}>{fmt(r.IGSTAmount)}</td>
                      <td style={{ ...tdR, color: '#dc2626' }}>{fmt(r.DiscountAmount)}</td>
                      <td style={tdR}>{fmt(r.FreightAmount)}</td>
                      <td style={{ ...tdR, fontWeight: 700 }}>{fmt(r.GrandTotal)}</td>
                      <td style={tdC}>
                        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: pc.bg, color: pc.color }}>
                          {r.PaymentType || '-'}
                        </span>
                      </td>
                      <td style={tdC}>
                        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: sc.bg, color: sc.color }}>
                          {r.PaymentStatus || '-'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr style={{ background: PRIMARY_LT, color: PRIMARY, fontWeight: 700 }}>
                  <td colSpan={5} style={{ ...td, textAlign: 'right', fontWeight: 700 }}>Totals</td>
                 
                  <td style={tdR}>{fmt(totalTaxable)}</td>
                  <td style={tdR}>{fmt(totalCGST)}</td>
                  <td style={tdR}>{fmt(totalSGST)}</td>
                  <td style={tdR}>{fmt(totalIGST)}</td>
                  <td style={tdR}>{fmt(totalDiscount)}</td>
                  <td style={td}></td>
                  <td style={tdR}>{fmt(totalGrand)}</td>
                  <td colSpan={2} style={td}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default SaleSummaryBillWise;




